import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, Info } from 'lucide-react';
import { useDrawerEscape } from '../../lib/hooks/useDrawerEscape';
import './organization-settings.css';

/**
 * Also remains usable inside a disabled fieldset: help is available in read-only settings.
 * `icon="info"` mostra la «i» al posto del «?», per le spiegazioni generali di una pagina.
 */
export function SettingsHelp({ label, children, icon = 'help' }: { label: string; children: ReactNode; icon?: 'help' | 'info' }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 16, top: 16 });
  const trigger = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const keepOpen = () => { clearTimeout(timer.current); setOpen(true); };
  const closeSoon = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 160); };
  useDrawerEscape(open, () => setOpen(false), 30000);
  useEffect(() => () => clearTimeout(timer.current), []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = trigger.current?.getBoundingClientRect();
      if (!anchor || !panel.current) return;
      const width = panel.current.offsetWidth;
      const height = panel.current.offsetHeight;
      setPosition({
        left: Math.max(16, Math.min(anchor.left - 8, window.innerWidth - width - 16)),
        top: anchor.bottom + height + 12 <= window.innerHeight ? anchor.bottom + 8 : Math.max(16, anchor.top - height - 8),
      });
    };
    place();
    const outside = (event: PointerEvent) => {
      if (!trigger.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('pointerdown', outside, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('pointerdown', outside, true);
    };
  }, [open]);
  return <>
    <span ref={trigger} role="button" tabIndex={0} className="org-settings-help"
      aria-label={`Spiegazione: ${label}`} aria-expanded={open} aria-describedby={open ? id : undefined}
      onPointerEnter={event => { if (event.pointerType === 'mouse') keepOpen(); }}
      onPointerLeave={event => { if (event.pointerType === 'mouse') closeSoon(); }}
      onFocus={event => { if (event.currentTarget.matches(':focus-visible')) keepOpen(); }}
      onBlur={() => setOpen(false)}
      onClick={event => { event.preventDefault(); event.stopPropagation(); keepOpen(); }}
      onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); keepOpen(); } }}>
      {icon === 'info' ? <Info size={16} aria-hidden="true" /> : <HelpCircle size={14} aria-hidden="true" />}
    </span>
    {open && createPortal(<div ref={panel} id={id} role="tooltip" className="org-settings org-settings-help-popover"
      style={position} onPointerEnter={keepOpen} onPointerLeave={closeSoon}>
      <strong>{label}</strong><div>{children}</div>
    </div>, document.body)}
  </>;
}
